// Hoe een meter op het scherm komt te staan (Spoor B, sessie B2).
//
// Alleen opmaak. De server rekent (waarde, verschil, stand, reden); hier wordt
// een getal een Nederlands getal en krijgt het zijn eenheid. Twee regels die
// dat scheiden bewaken:
//
//  1. Een aandeel komt als breuk van de server (0,5714). Er honderd keer
//     nemen is opmaak — hetzelfde getal, andere notatie. Teller en noemer
//     bij elkaar optellen, delen of afronden naar een oordeel is dat NIET.
//  2. Een verschil krijgt nooit een kleur. Meer is niet vanzelf beter: bij
//     "Netheid" (K4) is een stijging juist slecht. Welke kant goed is, zegt
//     alleen het stoplicht van de server.

import type { MeterkastMeter, MeterkastMeting } from "../../contracts";

type Eenheid = MeterkastMeter["eenheid"];

/** "1.069" — Nederlandse duizendtallen, zoals de rest van de beheervlakken. */
const getal = (n: number) => n.toLocaleString("nl-NL");

/** Een breuk van de server als percentage: 0,5714 → "57%", 0,0484 → "4,8%".
 *  Eén decimaal onder de tien procent, anders leest 5% als een ander getal
 *  dan 4,8%. */
export function aandeel(fractie: number): string {
  const p = fractie * 100;
  const decimalen = Math.abs(p) < 10 ? 1 : 0;
  return `${p.toLocaleString("nl-NL", { maximumFractionDigits: decimalen })}%`;
}

/** De waarde van één meting, in de eenheid van zijn meter. Null als de server
 *  geen waarde stuurde — dan staat er een reden, geen nul. */
export function waardeTekst(meting: MeterkastMeting | null, eenheid: Eenheid): string | null {
  if (!meting || meting.waarde === null) return null;
  const w = meting.waarde;
  switch (eenheid) {
    case "aandeel":
      return aandeel(w);
    case "credits":
      return `${w.toLocaleString("nl-NL", { maximumFractionDigits: 1 })} credits`;
    case "cijfer":
      return w.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    default:
      return getal(w);
  }
}

/** "12 van 21" — de breuk waarop een aandeel rust, of de noemer bij een
 *  aantal. Null als de server er geen stuurde; dan staat alleen de waarde. */
export function breukTekst(meting: MeterkastMeting | null): string | null {
  if (!meting || meting.teller === null || meting.noemer === null || meting.noemer === 0) {
    return null;
  }
  return `${getal(meting.teller)} van ${getal(meting.noemer)}`;
}

/**
 * Het verschil met de basis, zoals de server het uitrekende (huidig − basis).
 * Bij een aandeel is dat een verschil tussen twee breuken, dus procentpunten —
 * geen procenten. Dat woord staat voluit in `verschilUitleg`, want "pp" zegt
 * niemand iets die het niet al weet.
 */
export function verschilTekst(verschil: number | null, eenheid: Eenheid): string | null {
  if (verschil === null || verschil === 0) return verschil === 0 ? "gelijk" : null;
  const teken = verschil > 0 ? "+" : "−";
  const abs = Math.abs(verschil);
  switch (eenheid) {
    case "aandeel":
      // Het verschil tussen twee breuken: 0,57 − 0,50 = 0,07 → "+7 pp". Dus
      // dezelfde omrekening als de waarde zelf, maar met "pp" erachter: zeven
      // PROCENTPUNT, niet zeven procent.
      return `${teken}${(abs * 100).toLocaleString("nl-NL", { maximumFractionDigits: 1 })} pp`;
    case "cijfer":
      return `${teken}${abs.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case "credits":
      return `${teken}${abs.toLocaleString("nl-NL", { maximumFractionDigits: 1 })}`;
    default:
      return `${teken}${getal(abs)}`;
  }
}

/** De uitleg bij een verschil, voor de tooltip en de schermlezer. */
export function verschilUitleg(verschil: number | null, eenheid: Eenheid): string | null {
  if (verschil === null) return null;
  if (verschil === 0) return "gelijk aan de vorige stand";
  const richting = verschil > 0 ? "meer" : "minder";
  if (eenheid === "aandeel") {
    const pp = (Math.abs(verschil) * 100).toLocaleString("nl-NL", { maximumFractionDigits: 1 });
    return `${pp} procentpunt ${richting} dan de vorige stand`;
  }
  return `${richting} dan de vorige stand`;
}
